import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiAlertTriangle, FiCheckCircle, FiUploadCloud, FiXCircle } from 'react-icons/fi';

const getStatusMeta = (status) => {
  if (status === 'approved') return { cls: 'status-booking-approved', icon: <FiCheckCircle />, label: 'Approved' };
  if (status === 'rejected') return { cls: 'status-booking-rejected', icon: <FiXCircle />, label: 'Rejected' };
  return { cls: 'status-booking-pending', icon: <FiAlertTriangle />, label: 'Pending' };
};

const PartnerKycPage = ({ embedded = false, onApproved = null }) => {
  const [loading, setLoading] = useState(true);
  const [docTypes, setDocTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [kycData, setKycData] = useState({ partner_kyc_status: 'not_submitted', latest_request: null, kyc_rejection_reason: '' });

  const status = useMemo(() => kycData.partner_kyc_status || 'not_submitted', [kycData.partner_kyc_status]);
  const latest = kycData.latest_request;
  const statusMeta = getStatusMeta(latest?.status || status);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const fetchKycState = useCallback(async () => {
    const [typesRes, stateRes] = await Promise.all([
      axios.get(`${API_URL}/kyc/document-types`),
      axios.get(`${API_URL}/kyc/my`, { headers: authHeaders })
    ]);
    setDocTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
    setKycData(stateRes.data || {});
  }, [authHeaders]);

  useEffect(() => {
    (async () => {
      try {
        await fetchKycState();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to load KYC details');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchKycState]);

  useEffect(() => {
    if (status === 'approved' && typeof onApproved === 'function') onApproved();
  }, [status, onApproved]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDocType) return toast.error('Please select a document type');
    if (!selectedFile) return toast.error('Please upload your document');
    if (!/\.(pdf|png|jpe?g|webp)$/i.test(selectedFile.name)) {
      return toast.error('Only PDF or image files are allowed');
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', selectedFile);
      const uploadRes = await axios.post(`${API_URL}/upload/single`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      const fileName = uploadRes.data?.path?.split('/').pop() || uploadRes.data?.filename;
      await axios.post(`${API_URL}/kyc/submit`, {
        document_type_id: Number(selectedDocType),
        document_file: fileName
      }, { headers: authHeaders });
      toast.success('KYC request submitted successfully');
      setSelectedFile(null);
      setSelectedDocType('');
      await fetchKycState();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit KYC request');
    } finally {
      setSubmitting(false);
    }
  };

  const card = (
    <div className="admin-card" style={{ maxWidth: 560, width: '100%', margin: 0 }}>
      <div className="page-header" style={{ marginBottom: '1rem' }}>
        <h1 style={{ fontSize: '1.4rem', marginBottom: '0.25rem' }}>Partner KYC Verification</h1>
        <p>Complete KYC to access partner dashboard features.</p>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 180, borderRadius: 8 }} />
      ) : status === 'pending' || status === 'approved' ? (
        <div className={`admin-card ${statusMeta.cls}`} style={{ border: '1px solid', padding: '1rem', display: 'flex', gap: '0.75rem' }}>
          <div style={{ fontSize: '1.25rem', marginTop: 2 }}>{statusMeta.icon}</div>
          <div>
            <strong>{statusMeta.label}</strong>
            <p style={{ marginTop: '0.4rem' }}>
              {status === 'pending'
                ? 'Your KYC is under review. You will be notified once reviewed.'
                : 'Your KYC is approved. Dashboard access is now enabled.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {(status === 'rejected' || kycData.kyc_rejection_reason) && (
            <div className="admin-card status-booking-rejected" style={{ border: '1px solid', marginBottom: '1rem', padding: '0.875rem' }}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiXCircle /> Rejected</strong>
              <p style={{ marginTop: '0.45rem' }}>
                {kycData.kyc_rejection_reason || latest?.admin_note || 'Please re-submit with valid document.'}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Select Document Type *</label>
              <select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value)} required>
                <option value="">Choose document type</option>
                {docTypes.map((doc) => (
                  <option key={doc.id} value={doc.id}>{doc.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Upload Document (PDF/Image) *</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} required />
            </div>
            <button type="submit" className="btn-primary" disabled={submitting}>
              <FiUploadCloud /> {submitting ? 'Submitting...' : 'Submit KYC Request'}
            </button>
          </form>
        </>
      )}
    </div>
  );

  if (!embedded) return <div className="admin-page">{card}</div>;
  return card;
};

export default PartnerKycPage;
